import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';
import { SelectButton } from '@verclam/ui-kit';

const pillsStyle = css`
	--ui-kit-select-button-input-gap: 0.375rem;

	.ui-kit-select-button-option {
		--ui-kit-select-button-option-radius: ${theme.radii.pill};
		--ui-kit-select-button-option-padding: 0.5625rem 0.875rem;
		--ui-kit-select-button-option-min-height: 0;
		--ui-kit-select-button-option-font-size: 0.78125rem;
		--ui-kit-select-button-option-font-weight: ${theme.fontWeights.medium};
		--ui-kit-select-button-option-border-width: 1.5px;
		--ui-kit-select-button-option-bg: ${theme.colors.surface};
		--ui-kit-select-button-option-text: ${theme.colors.inkSecondary};
		--ui-kit-select-button-option-border: ${theme.colors.borderStrong};
		--ui-kit-select-button-option-border-hover: ${theme.colors.accent};
	}

	.ui-kit-select-button-option[data-pressed] {
		--ui-kit-select-button-option-font-weight: ${theme.fontWeights.semibold};
		--ui-kit-select-button-option-bg: ${theme.colors.accent};
		--ui-kit-select-button-option-bg-hover: ${theme.colors.accentDark};
		--ui-kit-select-button-option-text: ${theme.colors.surface};
		--ui-kit-select-button-option-border: ${theme.colors.accent};
		--ui-kit-select-button-option-border-hover: ${theme.colors.accentDark};
	}
`;

type PillOption<TValue> = {
	readonly label: string;
	readonly value: TValue;
};

type SharedProps<TValue> = {
	readonly name: string;
	/** Names the group for assistive technologies; shown above the pills unless hidden. */
	readonly label: string;
	readonly isLabelHidden?: boolean;
	readonly options: readonly PillOption<TValue>[];
};

type SingleProps<TValue> = SharedProps<TValue> & {
	readonly multiple?: false;
	readonly value: TValue;
	readonly onChange: (value: TValue) => void;
};

type MultipleProps<TValue> = SharedProps<TValue> & {
	readonly multiple: true;
	readonly value: readonly TValue[];
	readonly onChange: (value: readonly TValue[]) => void;
};

type Props<TValue> = SingleProps<TValue> | MultipleProps<TValue>;

/**
 * Options as a row of pills: one is always selected (single), or any number is (multiple). Wraps the
 * kit's `SelectButton` and only restyles it through the kit's own variables.
 */
function PillChoice<TValue extends string | number>(props: Props<TValue>): React.ReactElement {
	const { name, label, isLabelHidden, options } = props;

	const handleChange = React.useCallback(
		(next: TValue | readonly TValue[] | null) => {
			if (next === null) {
				return;
			}

			if (props.multiple === true) {
				props.onChange(next as readonly TValue[]);
				return;
			}

			props.onChange(next as TValue);
		},
		[props],
	);

	return (
		<div css={pillsStyle}>
			<SelectButton<TValue>
				name={name}
				label={label}
				isLabelHidden={isLabelHidden}
				options={options}
				optionLabel="label"
				optionValue="value"
				multiple={props.multiple === true}
				allowEmpty={props.multiple === true}
				value={props.value}
				onChange={handleChange}
			/>
		</div>
	);
}

export default PillChoice;
